/**
 * Experience profiles — Settings › People & Agents
 *
 * Route: /prioritization/settings/experience-profiles
 *
 * How the product presents itself to each member: the full product, or
 * Guided — a single home page (Today) that says who they are, what needs
 * them, and where the plan stands, one action at a time. Set here by an
 * admin, stored on the membership row (migration 795), honoured on every
 * device. The member can switch themselves back from Today.
 */

import { useEffect, useMemo, useState } from 'react';
import { Compass, Loader2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { Select } from '@/components/ds';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { listWorkspaceMembers } from '../../../lib/workspaceService';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import {
  EXPERIENCE_PROFILE_DESCRIPTIONS,
  EXPERIENCE_PROFILE_LABELS,
  isExperienceProfile,
  setExperienceProfile,
  type ExperienceProfile,
} from '../../../lib/experienceProfile';

interface MemberRow {
  userId: string;
  name: string;
  email: string;
  role: string;
  profile: ExperienceProfile;
}

export default function ExperienceProfiles() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [rows, setRows] = useState<MemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, 'saving' | 'saved' | 'error'>>({});
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [members, profiles] = await Promise.all([
          listWorkspaceMembers(workspaceId),
          getSupabaseClient().from('workspace_memberships').select('user_id, experience_profile').eq('workspace_id', workspaceId),
        ]);
        if (cancelled) return;
        const profileByUser = new Map<string, ExperienceProfile>();
        for (const p of (profiles.data ?? []) as Array<{ user_id: string; experience_profile?: unknown }>) {
          profileByUser.set(p.user_id, isExperienceProfile(p.experience_profile) ? p.experience_profile : 'full');
        }
        setRows(
          members.map((m) => ({
            userId: m.userId,
            name: m.user?.name || m.user?.email || m.userId,
            email: m.user?.email || '',
            role: m.role,
            profile: profileByUser.get(m.userId) ?? 'full',
          })),
        );
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load members.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)) : rows;
  }, [rows, query]);

  const change = async (userId: string, profile: ExperienceProfile) => {
    if (!workspaceId) return;
    const before = rows.find((r) => r.userId === userId)?.profile;
    setRows((prev) => prev.map((r) => (r.userId === userId ? { ...r, profile } : r)));
    setSaving((prev) => ({ ...prev, [userId]: 'saving' }));
    try {
      await setExperienceProfile(workspaceId, userId, profile);
      setSaving((prev) => ({ ...prev, [userId]: 'saved' }));
      setTimeout(() => setSaving((prev) => { const next = { ...prev }; delete next[userId]; return next; }), 2000);
    } catch (err) {
      setRows((prev) => prev.map((r) => (r.userId === userId && before ? { ...r, profile: before } : r)));
      setSaving((prev) => ({ ...prev, [userId]: 'error' }));
      setError(err instanceof Error ? err.message : 'Could not save that.');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Compass className="h-4 w-4 text-[var(--text-ter)]" />}
        title="Experience profiles"
        subtitle="How the product presents itself to each member. Guided is for people who are not technical, or who need every visit to start from zero."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(Object.keys(EXPERIENCE_PROFILE_LABELS) as ExperienceProfile[]).map((p) => (
          <div key={p} className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4">
            <p className="font-heading font-semibold text-sm text-[var(--text-pri)]" style={{ color: 'var(--text-pri)' }}>{EXPERIENCE_PROFILE_LABELS[p]}</p>
            <p className="text-sm text-[var(--text-sec)] mt-1">{EXPERIENCE_PROFILE_DESCRIPTIONS[p]}</p>
          </div>
        ))}
      </div>

      {error && (
        <div role="alert" className="border border-[var(--coral)]/30 bg-[var(--coral)]/10 px-4 py-3 text-sm text-[var(--coral)]">{error}</div>
      )}

      <div className="max-w-sm">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a member"
          aria-label="Find a member"
          className="w-full h-9 px-3 text-sm border border-[var(--rule)] bg-[var(--surface)] text-[var(--text-pri)] placeholder:text-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-[var(--slate)]"
        />
      </div>

      {loading ? (
        <p className="text-sm text-[var(--text-sec)] flex items-center gap-2" aria-busy="true">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
        </p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-[var(--text-sec)]">{rows.length === 0 ? 'No members yet.' : `Nobody matches "${query}".`}</p>
      ) : (
        <ul className="divide-y divide-[var(--rule)] border border-[var(--rule)] bg-[var(--bg-raised)]">
          {visible.map((r) => (
            <li key={r.userId} className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-pri)] truncate">{r.name}</p>
                <p className="text-xs text-[var(--text-ter)] truncate">{r.email}{r.role ? ` · ${r.role}` : ''}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs w-12 text-right">
                  {saving[r.userId] === 'saving' && <span className="text-[var(--text-dim)]">Saving…</span>}
                  {saving[r.userId] === 'saved' && <span className="text-[var(--green)] dark:text-[var(--text-ter)]">Saved</span>}
                  {saving[r.userId] === 'error' && <span className="text-[var(--coral)]">Not saved</span>}
                </span>
                <Select
                  aria-label={`Experience profile for ${r.name}`}
                  value={r.profile}
                  onChange={(e) => void change(r.userId, e.target.value as ExperienceProfile)}
                >
                  {(Object.keys(EXPERIENCE_PROFILE_LABELS) as ExperienceProfile[]).map((p) => (
                    <option key={p} value={p}>{EXPERIENCE_PROFILE_LABELS[p]}</option>
                  ))}
                </Select>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
